import type { components } from '@/lib/api/generated/schema';
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  apiUpload,
} from '@/lib/api/client';

type Schemas = components['schemas'];

export type ShopProfile = Schemas['ShopProfileOut'];
export type OwnerLanguage = Schemas['OwnerLanguageOut'];
export type OwnerCategory = Schemas['OwnerCategoryOut'];
export type Dish = Schemas['DishOut'];
export type Asset = Schemas['AssetOut'];
export type Translation = Schemas['TranslationOut'];
export type PublishResult = Schemas['PublishResultOut'];
export type OpenDecision = Schemas['PriceValidationNote'];

export interface TranslationInput {
  language_code: string;
  name: string;
  description?: string | null;
  ingredients_text?: string | null;
  cooking_instructions?: string | null;
}

const base = (slug: string) => `/owner/${encodeURIComponent(slug)}`;

export function fetchOwnerShop(slug: string, signal?: AbortSignal): Promise<ShopProfile> {
  return apiGet<ShopProfile>(`${base(slug)}/shop`, signal);
}

export interface ShopProfileUpdate {
  name?: string;
  headline?: string | null;
  name_ja?: string | null;
  owner_name?: string | null;
  location?: string | null;
  contact_email?: string | null;
  default_language?: string;
  languages?: string[];
}

export function updateOwnerShop(
  slug: string,
  patch: ShopProfileUpdate,
): Promise<ShopProfile> {
  return apiPatch<ShopProfile>(`${base(slug)}/shop`, patch);
}

export function fetchOpenDecisions(slug: string, signal?: AbortSignal): Promise<OpenDecision[]> {
  return apiGet<OpenDecision[]>(`${base(slug)}/open-decisions`, signal);
}

export function fetchAssets(
  slug: string,
  assetType?: string,
  signal?: AbortSignal,
): Promise<Asset[]> {
  const query = assetType ? `?asset_type=${encodeURIComponent(assetType)}` : '';
  return apiGet<Asset[]>(`${base(slug)}/assets${query}`, signal);
}

export function uploadAsset(
  slug: string,
  file: File,
  assetType = 'dish_photo',
  altText?: string,
): Promise<Asset> {
  const fields: Record<string, string> = { asset_type: assetType };
  if (altText) fields['alt_text'] = altText;
  return apiUpload<Asset>(`${base(slug)}/assets`, file, fields);
}

export function setEntryImage(slug: string, assetId: string): Promise<Asset> {
  return apiPut<Asset>(`${base(slug)}/entry-image`, { asset_id: assetId });
}

export function setDishImage(
  slug: string,
  dishId: string,
  assetId: string | null,
): Promise<Dish> {
  return apiPut<Dish>(`${base(slug)}/dishes/${dishId}/image`, { asset_id: assetId });
}

export function fetchCategories(slug: string, signal?: AbortSignal): Promise<OwnerCategory[]> {
  return apiGet<OwnerCategory[]>(`${base(slug)}/categories`, signal);
}

export function createCategory(
  slug: string,
  translations: TranslationInput[],
  status = 'draft',
): Promise<OwnerCategory> {
  return apiPost<OwnerCategory>(`${base(slug)}/categories`, { translations, status });
}

export function updateCategory(
  slug: string,
  categoryId: string,
  patch: { translations?: TranslationInput[]; status?: string; sort_order?: number },
): Promise<OwnerCategory> {
  return apiPatch<OwnerCategory>(`${base(slug)}/categories/${categoryId}`, patch);
}

export function reorderCategories(slug: string, orderedIds: string[]): Promise<void> {
  return apiPut<void>(`${base(slug)}/categories/order`, { ordered_ids: orderedIds });
}

export function deleteCategory(
  slug: string,
  categoryId: string,
): Promise<{ deleted: boolean; dishes_removed: number }> {
  return apiDelete<{ deleted: boolean; dishes_removed: number }>(
    `${base(slug)}/categories/${categoryId}`,
  );
}

export function publishCategory(slug: string, categoryId: string): Promise<PublishResult> {
  return apiPost<PublishResult>(`${base(slug)}/categories/${categoryId}/publish`);
}

export function fetchDishes(
  slug: string,
  categoryId?: string,
  signal?: AbortSignal,
): Promise<Dish[]> {
  const query = categoryId ? `?category_id=${encodeURIComponent(categoryId)}` : '';
  return apiGet<Dish[]>(`${base(slug)}/dishes${query}`, signal);
}

export function fetchDish(slug: string, dishId: string, signal?: AbortSignal): Promise<Dish> {
  return apiGet<Dish>(`${base(slug)}/dishes/${dishId}`, signal);
}

export interface DishCreate {
  category_id: string;
  translations: TranslationInput[];
  price_amount?: string | null;
  currency?: string | null;
  status?: string;
}

export function createDish(slug: string, payload: DishCreate): Promise<Dish> {
  return apiPost<Dish>(`${base(slug)}/dishes`, payload);
}

export interface DishUpdate {
  category_id?: string;
  translations?: TranslationInput[];
  price_amount?: string | null;
  currency?: string | null;
  status?: string;
  sort_order?: number;
}

export function updateDish(slug: string, dishId: string, patch: DishUpdate): Promise<Dish> {
  return apiPatch<Dish>(`${base(slug)}/dishes/${dishId}`, patch);
}

export function deleteDish(slug: string, dishId: string): Promise<void> {
  return apiDelete<void>(`${base(slug)}/dishes/${dishId}`);
}

export function updateDishContent(
  slug: string,
  dishId: string,
  payload: {
    language_code: string;
    description?: string | null;
    ingredients_text?: string | null;
    cooking_instructions?: string | null;
  },
): Promise<Dish> {
  return apiPatch<Dish>(`${base(slug)}/dishes/${dishId}/content`, payload);
}

export function displayName(
  translations: Translation[],
  preferred = 'en',
): string {
  const order = [preferred, 'en', ...translations.map((t) => t.language_code)];
  for (const code of order) {
    const match = translations.find((t) => t.language_code === code);
    if (match?.name) return match.name;
  }
  return '(untitled)';
}

export function translationFor(
  translations: Translation[],
  languageCode: string,
): Translation | undefined {
  return translations.find((t) => t.language_code === languageCode);
}

export function hasUnapprovedDraft(translations: Translation[]): boolean {
  return translations.some((t) => t.translation_status === 'machine_draft');
}

export type SignagePattern = Schemas['SignagePatternOut'];
export type SignagePreset = Schemas['SignagePresetOut'];
export type SignageFormat = Schemas['SignageFormatOut'];
export type SignageRender = Schemas['SignageRenderOut'];
export type SignageOptions = Schemas['SignageOptionsOut'];

export function fetchSignageOptions(
  slug: string,
  signal?: AbortSignal,
): Promise<SignageOptions> {
  return apiGet<SignageOptions>(`${base(slug)}/signage-options`, signal);
}

export interface SignageRenderInput {
  pattern_id: string;
  preset: string;
  output_format: string;
  language_code: string;
  headline?: string | null;
}

export function createSignageRender(
  slug: string,
  input: SignageRenderInput,
): Promise<SignageRender> {
  return apiPost<SignageRender>(`${base(slug)}/signage-renders`, input);
}

export function fetchSignageRenders(
  slug: string,
  signal?: AbortSignal,
): Promise<SignageRender[]> {
  return apiGet<SignageRender[]>(`${base(slug)}/signage-renders`, signal);
}

export type SampleText = {
  id: string;
  language_code: string;
  title: string;
  body: string;
};

export type TranslationResult = {
  text: string;
  source_language: string;
  target_language: string;
  warnings: string[];
  available: boolean;
  message: string | null;
};

export type DraftResultOut = {
  dish_id: string;
  name: string;
  description: string;
  ingredients_text: string | null;
  is_draft: boolean;
  warnings: string[];
  available: boolean;
  message: string | null;
};

export function fetchSamples(
  languageCode: string,
  signal?: AbortSignal,
): Promise<SampleText[]> {
  return apiGet<SampleText[]>(`/owner/samples?language_code=${languageCode}`, signal);
}

export function translateText(
  shopSlug: string,
  payload: {
    text: string;
    source_language: string;
    target_language: string;
    request_id: string;
  },
): Promise<TranslationResult> {
  return apiPost<TranslationResult>(`/owner/${shopSlug}/translate`, payload);
}

export function generateDraft(
  shopSlug: string,
  payload: { dish_id: string; language_code: string; request_id: string },
): Promise<DraftResultOut> {
  return apiPost<DraftResultOut>(`/owner/${shopSlug}/generate-draft`, payload);
}

export function transcribeAudio(
  shopSlug: string,
  audio: Blob,
  languageCode: string,
): Promise<{
  available: boolean;
  text: string;
  language: string | null;
  message: string | null;
  warnings: string[];
}> {
  const body = new FormData();
  body.append('audio', audio, 'voice.webm');
  body.append('language_code', languageCode);
  return apiPost(`/owner/${shopSlug}/transcribe`, body);
}
